import { CardShell } from '@atlas/ui-components'
import { Button, Chip, Spinner } from '@atlas/ui-primitives'
import { useState } from 'react'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { AddTestCaseDialog } from '@/components/prompts/AddTestCaseDialog'
import type { EvalRun, Prompt, PromptTestCase } from '@/domain/prompt.types'
import {
	ASSERTION_TYPE_LABEL,
	EvalCaseOutcome,
	EvalRunStatus,
	isRunSettled,
	PromptTestCaseOrigin,
} from '@/domain/prompt.types'
import {
	useCreateTestCase,
	useDeleteTestCase,
	useEvalRun,
	useGenerateTestCases,
	usePromptAsyncResults,
	usePromptEvalRuns,
	usePromptLists,
	usePromptTestCases,
	useRequestEvalRun,
} from '@/hooks/usePrompts'
import { notification } from '@/notifications/notification-store'

const OUTCOME_COLOR: Record<EvalCaseOutcome, string> = {
	[EvalCaseOutcome.PASSED]: 'text-ds-success',
	[EvalCaseOutcome.FAILED]: 'text-ds-danger',
	// Errored is a setup/infrastructure problem, not a verdict on the prompt — warning
	// colour keeps it visually distinct from a real assertion failure.
	[EvalCaseOutcome.ERRORED]: 'text-ds-warning',
}

const STATUS_LABEL: Record<EvalRunStatus, string> = {
	[EvalRunStatus.PENDING]: 'Queued',
	[EvalRunStatus.RUNNING]: 'Running',
	[EvalRunStatus.COMPLETED]: 'Completed',
	[EvalRunStatus.FAILED]: 'Failed',
	[EvalRunStatus.CANCELLED]: 'Cancelled',
}

export function PromptEvalTab({ prompt }: { prompt: Prompt }) {
	const cases = usePromptTestCases(prompt.id)
	const runs = usePromptEvalRuns(prompt.id)
	const requestRun = useRequestEvalRun()
	const generate = useGenerateTestCases()
	const removeCase = useDeleteTestCase()

	const addCase = useCreateTestCase()
	const lists = usePromptLists()
	const [adding, setAdding] = useState(false)

	/** The run just triggered, watched until it settles. */
	const [watchedRunId, setWatchedRunId] = useState<string | null>(null)
	// The prompt id is what the realtime subscription addresses — the events carry a
	// `prompt` entityRef, so the run id is not addressable as a topic.
	const watched = useEvalRun(watchedRunId, prompt.id)

	// Generated cases and drafted schemas arrive with no run row to poll; before this
	// they only showed up on a manual reload.
	usePromptAsyncResults(prompt.id)

	const latest = watched.data ?? (runs.data ?? [])[0]

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<Button
					isDisabled={requestRun.isPending || (cases.data ?? []).length === 0}
					onPress={() =>
						requestRun.mutate(prompt.id, {
							// The response is an ACCEPTANCE, not a result — start watching the
							// run rather than expecting outcomes here.
							onSuccess: (accepted) => setWatchedRunId(accepted.runId),
						})
					}
				>
					{requestRun.isPending ? 'Queueing…' : 'Run evals'}
				</Button>
				<Button
					variant="secondary"
					isDisabled={generate.isPending}
					onPress={() =>
						// Drafting starts a billable model job and its results arrive later over
						// realtime. Without these the button simply greyed out: a refused request
						// looked identical to one still working.
						generate.mutate(prompt.id, {
							onSuccess: () => notification.info('Drafting requested. Proposals appear as they are generated.'),
							onError: (error) =>
								notification.error(extractApiErrorMessage(error, 'Test cases could not be requested.')),
						})
					}
				>
					{generate.isPending ? 'Requesting…' : 'Generate test cases'}
				</Button>
				{/*
					Always available, because drafting is asynchronous and needs a model
					credential — a prompt with no cases cannot be evaluated, and the publish
					gate reads eval results.
				*/}
				<Button variant="secondary" onPress={() => setAdding(true)}>
					Add a case
				</Button>
			</div>

			{requestRun.isError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					The run could not be queued — it may be rate-limited, or the prompt may have no active cases.
				</p>
			) : null}
			{generate.isSuccess && (cases.data ?? []).length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">
					Drafting requested. Proposals appear here as generated cases once ready.
				</p>
			) : null}

			{latest ? <RunSummary run={latest} /> : null}

			<TestCaseList
				cases={cases.data ?? []}
				isLoading={cases.isLoading}
				// Drafting is asynchronous with no completion signal of its own, so "in
				// flight" is "accepted, and no case has arrived yet".
				isDrafting={generate.isSuccess}
				onDelete={(caseId) =>
					removeCase.mutate(
						{ promptId: prompt.id, caseId },
						{
							onSuccess: () => notification.success('Test case deleted.'),
							onError: (error) =>
								notification.error(extractApiErrorMessage(error, 'The test case could not be deleted.')),
						},
					)
				}
			/>

			<AddTestCaseDialog
				open={adding}
				variables={prompt.variables}
				lists={lists.data ?? []}
				submitting={addCase.isPending}
				errorMessage={addCase.isError ? 'The case could not be added. Your entries are still here.' : null}
				onCancel={() => {
					setAdding(false)
					addCase.reset()
				}}
				onCreate={(input) => {
					addCase.mutate(
						{ id: prompt.id, input: { ...input, assertions: [] } },
						{
							onSuccess: () => {
								setAdding(false)
								notification.success(`Test case “${input.name}” added.`)
							},
						},
					)
				}}
			/>
		</div>
	)
}

function RunSummary({ run }: { run: EvalRun }) {
	const settled = isRunSettled(run.status)

	return (
		<CardShell as="section" aria-label="Latest eval run" className="p-4">
			<header className="mb-2 flex items-center justify-between">
				<h3 className="text-ds-sm font-medium text-ds-text">Latest run</h3>
				<Chip variant={run.status === EvalRunStatus.FAILED ? 'danger' : settled ? 'muted' : 'primary'} size="sm">
					{STATUS_LABEL[run.status]}
				</Chip>
			</header>

			{settled ? (
				<p className="text-ds-sm text-ds-text-muted">
					<span className="text-ds-success">{run.passedCases} passed</span> ·{' '}
					<span className={run.failedCases > 0 ? 'text-ds-danger' : undefined}>{run.failedCases} failed</span>
					{run.erroredCases > 0 ? <span className="text-ds-warning"> · {run.erroredCases} errored</span> : null}
				</p>
			) : (
				// Progress, not a spinner: a run's duration scales with its case count, so
				// "3 of 8" is more useful than an indeterminate wait.
				<p className="text-ds-sm text-ds-text-muted">
					{run.completedCases} of {run.totalCases} cases complete…
				</p>
			)}

			{run.failureReason ? (
				<p role="alert" className="mt-2 text-ds-sm text-ds-danger">
					{run.failureReason}
				</p>
			) : null}

			{run.results.length > 0 ? (
				<ul className="mt-3 space-y-1">
					{run.results.map((r) => (
						<li key={r.testCaseId} className="flex items-center gap-2 text-ds-sm">
							<span className={OUTCOME_COLOR[r.outcome]}>{r.outcome}</span>
							<span className="text-ds-text-muted">{r.name}</span>
						</li>
					))}
				</ul>
			) : null}
		</CardShell>
	)
}

function TestCaseList({
	cases,
	isLoading,
	isDrafting,
	onDelete,
}: {
	cases: PromptTestCase[]
	isLoading: boolean
	/** A drafting request is in flight, so an empty list is "working", not "nothing". */
	isDrafting: boolean
	onDelete: (caseId: string) => void
}) {
	if (isLoading) return <p className="text-ds-text-muted">Loading…</p>

	if (cases.length === 0) {
		return (
			<div className="rounded-ds-card border border-dashed border-ds-border p-8 text-center text-ds-text-muted">
				{isDrafting ? (
					<span className="inline-flex items-center gap-2">
						<Spinner size="sm" />
						Generating cases…
					</span>
				) : (
					'No test cases yet. Generate a draft set, or add one by hand.'
				)}
			</div>
		)
	}

	return (
		<ul className="space-y-3">
			{cases.map((testCase) => (
				<CardShell as="li" key={testCase.id} className="p-4">
					<div className="flex items-start justify-between gap-3">
						<div>
							<p className="text-ds-sm font-medium text-ds-text">{testCase.name}</p>
							<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">
								{Object.keys(testCase.values).length} value(s)
							</p>
						</div>
						<div className="flex items-center gap-2">
							{/* Generated cases stay visibly flagged until a human edits one. */}
							{testCase.origin === PromptTestCaseOrigin.GENERATED ? (
								<Chip variant="muted" size="sm">
									Generated
								</Chip>
							) : null}
							<Button size="sm" variant="muted" onPress={() => onDelete(testCase.id)}>
								Remove
							</Button>
						</div>
					</div>

					{testCase.assertions.length > 0 ? (
						<ul className="mt-2 flex flex-wrap gap-1.5">
							{testCase.assertions.map((a, i) => (
								<li key={i}>
									<Chip variant="muted" size="sm">
										{ASSERTION_TYPE_LABEL[a.type]}
										{a.value ? `: ${a.value}` : ''}
										{a.max !== undefined ? `: ${a.max}` : ''}
									</Chip>
								</li>
							))}
						</ul>
					) : (
						<p className="mt-2 text-ds-sm text-ds-text-muted">
							No assertions — this case checks only that the prompt renders and executes.
						</p>
					)}
				</CardShell>
			))}
		</ul>
	)
}
